import React, { useState, useMemo } from 'react';
import Form from 'react-bootstrap/Form';
import HomeLinks from "./HomeLinks";

const getYear = (filedDate) => {
  if (!filedDate) return null;
  const parts = filedDate.split('/');
  const month = parseInt(parts[0], 10);
  const year = parseInt(parts[2], 10);
  return month >= 11 ? year + 1 : year;
};

const Home = ({hbBillsData}) => {
  const [searchKeyword, setSearchKeyword] = useState('');
  const [activeYear, setActiveYear] = useState(2026);
  const [sortBy, setSortBy] = useState('impact');
  const [selectedCategories, setSelectedCategories] = useState(new Set());
  const [categorySearch, setCategorySearch] = useState('');
  const [showSingletons, setShowSingletons] = useState(false);

  const years = [...new Set(
    hbBillsData.map(b => getYear(b.filedDate)).filter(Boolean)
  )].sort((a, b) => b - a);

  const selectedYear = years.includes(activeYear) ? activeYear : (years[0] || 2026);

  const yearFiltered = hbBillsData.filter(b => getYear(b.filedDate) === selectedYear);

  const categoryCounts = useMemo(() => {
    const counts = {};
    yearFiltered.forEach(b => {
      if (b.category) counts[b.category] = (counts[b.category] || 0) + 1;
    });
    return counts;
  }, [yearFiltered]);

  // Sort by bill count descending, then alphabetically
  const categories = Object.keys(categoryCounts).sort(
    (a, b) => categoryCounts[b] - categoryCounts[a] || a.localeCompare(b)
  );

  const singletonCategories = categories.filter(c => categoryCounts[c] === 1);
  const multiCategories = categories.filter(c => categoryCounts[c] > 1);

  const baseCategories = showSingletons ? categories : multiCategories;

  const visibleCategories = categorySearch
    ? categories.filter(c => c.toLowerCase().includes(categorySearch.toLowerCase()))
    : baseCategories;

  const searched = yearFiltered.filter((bill) =>
    bill.title.toLowerCase().includes(searchKeyword.toLowerCase()) ||
    (bill.sponsor && bill.sponsor.toLowerCase().includes(searchKeyword.toLowerCase()))
  );

  const categoryFiltered = selectedCategories.size > 0
    ? searched.filter(b => b.category && selectedCategories.has(b.category))
    : searched;

  const sorted = [...categoryFiltered].sort((a, b) => {
    if (sortBy === 'impact') {
      const sa = a.impactScore ?? 0;
      const sb = b.impactScore ?? 0;
      return sb - sa;
    }
    return parseInt(a.bill_number.replace(/\D/g, '')) - parseInt(b.bill_number.replace(/\D/g, ''));
  });

  const toggleCategory = (cat) => {
    setSelectedCategories(prev => {
      const next = new Set(prev);
      if (next.has(cat)) next.delete(cat);
      else next.add(cat);
      return next;
    });
  };

  const clearCategories = () => setSelectedCategories(new Set());

  return (
    <div className="home-page">

      {/* Hero */}
      <div className="home-hero text-center">
        <h1 className="mb-2">Tennessee Bill Archive</h1>
        <p className="home-hero-sub mx-auto">
          <strong>TNBills.com</strong> keeps the 2026 legislative tracker available
          as an archive while the public homepage focuses on the November 3 midterm election guide.
        </p>
      </div>

      {/* Toolbar */}
      <div className="home-toolbar">
        <div className="home-toolbar-inner">
          <input
            className="form-control form-control-sm home-search"
            placeholder="Search bills or sponsors…"
            value={searchKeyword}
            onChange={e => setSearchKeyword(e.target.value)}
            aria-label="Search bills"
          />
          <div className="btn-group flex-shrink-0">
            {years.map(y => (
              <button
                key={y}
                onClick={() => { setActiveYear(y); clearCategories(); }}
                className={`btn btn-sm ${selectedYear === y ? 'btn-dark' : 'btn-outline-secondary'}`}
              >
                {y}
              </button>
            ))}
          </div>
          <div className="btn-group flex-shrink-0">
            <button
              onClick={() => setSortBy('impact')}
              className={`btn btn-sm ${sortBy === 'impact' ? 'btn-primary' : 'btn-outline-primary'}`}
            >
              Most Impactful
            </button>
            <button
              onClick={() => setSortBy('number')}
              className={`btn btn-sm ${sortBy === 'number' ? 'btn-primary' : 'btn-outline-primary'}`}
            >
              Bill #
            </button>
          </div>
        </div>
      </div>

      {/* Sidebar + content */}
      <div className="home-body">

        <aside className="category-sidebar">
          <div className="d-flex justify-content-between align-items-center mb-2">
            <strong className="sidebar-heading">
              Categories
              {selectedCategories.size > 0 && (
                <span className="badge bg-primary ms-2" style={{fontSize: '0.7rem'}}>
                  {selectedCategories.size}
                </span>
              )}
            </strong>
            {selectedCategories.size > 0 && (
              <button className="btn btn-link btn-sm p-0 text-danger" onClick={clearCategories}>
                Clear
              </button>
            )}
          </div>
          <Form.Control
            size="sm"
            placeholder="Filter categories…"
            value={categorySearch}
            onChange={e => setCategorySearch(e.target.value)}
            className="mb-2"
          />
          <div className="category-list">
            {visibleCategories.map(cat => (
              <div key={cat} className="category-check">
                <input
                  className="form-check-input"
                  type="checkbox"
                  id={`cat-${cat}`}
                  checked={selectedCategories.has(cat)}
                  onChange={() => toggleCategory(cat)}
                />
                <label className="form-check-label" htmlFor={`cat-${cat}`}>
                  <span className="cat-name">{cat}</span>
                  <span className="cat-count">{categoryCounts[cat]}</span>
                </label>
              </div>
            ))}
          </div>
          {!categorySearch && singletonCategories.length > 0 && (
            <button
              className="btn btn-link btn-sm p-0 mt-2 text-muted"
              onClick={() => setShowSingletons(s => !s)}
            >
              {showSingletons
                ? 'Hide 1-bill categories'
                : `+ ${singletonCategories.length} more (1 bill each)`}
            </button>
          )}
        </aside>

        <div className="home-results">
          {/* Active filter chips */}
          {selectedCategories.size > 0 && (
            <div className="active-filters mb-3">
              {[...selectedCategories].map(cat => (
                <button
                  key={cat}
                  className="filter-chip"
                  onClick={() => toggleCategory(cat)}
                  title={`Remove "${cat}" filter`}
                >
                  {cat} <span aria-hidden="true">×</span>
                </button>
              ))}
            </div>
          )}

          <p className="text-muted small mb-3">
            {sorted.length} bill{sorted.length !== 1 ? 's' : ''}
            {selectedCategories.size > 0 && ` across ${selectedCategories.size} categor${selectedCategories.size === 1 ? 'y' : 'ies'}`}
          </p>

          {selectedYear === 2026 && selectedCategories.size === 0 && (
            <div className="alert alert-info mb-4 text-start" role="alert">
              Visit the <a href="/" className="alert-link">Tennessee November 3 Midterm Election Guide</a> for voting dates and county resources. This archive remains here for bill lookup and reference.
            </div>
          )}

          <HomeLinks billsData={sorted} />
        </div>

      </div>
    </div>
  );
}

export default Home;
